/**
 * TourContext — Persistent state for the guided tour feature.
 *
 * Tracks which route tours the user has already seen (opened).
 * Modeled after TenantContext: localStorage persistence + React state.
 *
 * Storage key: 'tour_seen_routes'
 * Value:        JSON string of string[] (normalized pathnames)
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { TourContextValue } from '../tours/types';

const STORAGE_KEY = 'tour_seen_routes';

function getStoredSeenRoutes(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistSeenRoutes(routes: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
  } catch {
    // Storage unavailable — fail silently
  }
}

/**
 * Normalize a pathname for storage: strip trailing slashes
 * and collapse dynamic segments like /projects/123 → /projects/:id.
 * This way, navigating to /projects/456 also marks the tour as seen.
 */
export function normalizePathForTour(pathname: string): string {
  // Remove trailing slash
  let p = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  // Replace numeric segments with ':id'
  p = p.replace(/\/\d+/g, '/:id');
  return p;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

export function TourContextProvider({ children }: { children: React.ReactNode }) {
  const [seenRoutes, setSeenRoutes] = useState<string[]>(getStoredSeenRoutes);

  const hasSeen = useCallback(
    (pathname: string) => seenRoutes.includes(normalizePathForTour(pathname)),
    [seenRoutes],
  );

  const markSeen = useCallback((pathname: string) => {
    const normalized = normalizePathForTour(pathname);
    setSeenRoutes((prev) => {
      if (prev.includes(normalized)) return prev;
      const next = [...prev, normalized];
      persistSeenRoutes(next);
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setSeenRoutes([]);
    persistSeenRoutes([]);
  }, []);

  return (
    <TourContext.Provider value={{ hasSeen, markSeen, resetAll }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTourContext(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTourContext must be used inside TourContextProvider');
  }
  return ctx;
}
